import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrash,
    faArrowUp,
    faArrowDown,
    faScaleBalanced,
    faTriangleExclamation,
    faClockRotateLeft,
    faCircleCheck,
    faPaperPlane,
    faFloppyDisk,
    faFilePdf,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import HMSButton from '../../components/ui/HMSButton';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSModal from '../../components/ui/HMSModal';
import templateHubService from '../../services/templateHubService';
import ValuePreview from './ValuePreview';

const TERM_KINDS = [
    { value: 'heading', label: 'Heading' },
    { value: 'body', label: 'Body' },
    { value: 'note', label: 'Note' },
];

// Version status → HMSBadge tone. ARCHIVED takes the primitive's `cancelled`
// grey: the shared set has no `archived`, and both mean "superseded, no longer
// in play", which is the tone the local pill was already painting.
const STATUS_TONE = {
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    ARCHIVED: 'cancelled',
};

const formatTimestamp = (value) => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch (err) {
        return value;
    }
};

/**
 * Seed the editor state from the block's active value (or, when this block is
 * already awaiting approval, we do not seed — the modal renders read-only).
 */
const seedRows = (activeValue) => {
    if (Array.isArray(activeValue) && activeValue.length > 0) {
        return activeValue.map((item) => ({
            kind: item?.kind || 'body',
            text: item?.text || '',
        }));
    }
    return [{ kind: 'body', text: '' }];
};

/**
 * The block content editor. Chooses an editor by content_format:
 *   - terms_list : structured, reorderable clause rows.
 *   - plain/html : a plain textarea (html = raw ReportLab markup).
 * Below the editor sits the version history and any rejection reason.
 *
 * When the block already has a pending_version the modal is read-only and shows
 * "A change is awaiting approval" — a second concurrent pending change is blocked
 * by construction (and by the backend).
 */
const BlockEditorModal = ({ block, onClose, onSaved, onPreview, canPreview }) => {
    const { showSuccess, showError } = useFlashMessage();

    const isTermsList = block.content_format === 'terms_list';
    const isPending = Boolean(block.pending_version);
    const requiresApproval = Boolean(block.requires_approval);

    const [rows, setRows] = useState(() => seedRows(block.active_value));
    const [text, setText] = useState(() => (typeof block.active_value === 'string' ? block.active_value : ''));
    const [changeNote, setChangeNote] = useState('');
    const [versions, setVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(true);
    const [saving, setSaving] = useState(null); // 'draft' | 'submit' | null
    const [previewing, setPreviewing] = useState(false);

    const loadVersions = useCallback(async () => {
        setLoadingVersions(true);
        try {
            const data = await templateHubService.getVersions(block.id);
            setVersions(Array.isArray(data) ? data : []);
        } catch (err) {
            setVersions([]);
        } finally {
            setLoadingVersions(false);
        }
    }, [block.id]);

    useEffect(() => {
        loadVersions();
    }, [loadVersions]);

    // Latest version drives the rejection banner (versions come newest-first).
    const latestVersion = versions[0] || null;
    const latestRejected = latestVersion && latestVersion.status === 'REJECTED';

    const buildValue = useCallback(() => {
        if (isTermsList) {
            return rows
                .map((r) => ({ kind: r.kind, text: r.text.trim() }))
                .filter((r) => r.text !== '');
        }
        return text;
    }, [isTermsList, rows, text]);

    const valueIsEmpty = useMemo(() => {
        if (isTermsList) {
            return rows.every((r) => !r.text.trim());
        }
        return !text.trim();
    }, [isTermsList, rows, text]);

    // --- terms_list row operations ----------------------------------------
    const updateRow = (index, patch) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };
    const addRow = () => setRows((prev) => [...prev, { kind: 'body', text: '' }]);
    const removeRow = (index) => setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    const moveRow = (index, delta) => {
        setRows((prev) => {
            const target = index + delta;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    // --- Save / submit -----------------------------------------------------
    const validate = () => {
        if (!changeNote.trim()) {
            showError('A change note is required so reviewers know what changed.');
            return false;
        }
        if (valueIsEmpty) {
            showError('Add some content before saving.');
            return false;
        }
        return true;
    };

    const handleSaveDraft = async () => {
        if (!validate()) return;
        setSaving('draft');
        try {
            await templateHubService.saveDraft(block.id, {
                value: buildValue(),
                change_note: changeNote.trim(),
            });
            showSuccess('Draft saved. It stays private until you submit it.');
            setChangeNote('');
            await loadVersions();
            if (onSaved) onSaved();
        } catch (err) {
            showError(err?.response?.data?.error || 'Could not save the draft.');
        } finally {
            setSaving(null);
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving('submit');
        try {
            const result = await templateHubService.submitBlock(block.id, {
                value: buildValue(),
                change_note: changeNote.trim(),
            });
            if (result?.auto_approved) {
                showSuccess('Published — this content is now live.');
            } else {
                showSuccess('Submitted for approval. A designated approver must approve it before it goes live.');
            }
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            showError(err?.response?.data?.error || 'Could not submit the change.');
        } finally {
            setSaving(null);
        }
    };

    // Preview the CURRENT editor value live — sends the same shape as save
    // (terms_list -> [{kind,text}], plain/html -> string) so the render matches
    // what a submit would produce. Nothing is persisted.
    const handlePreview = async () => {
        setPreviewing(true);
        try {
            await onPreview(
                () => templateHubService.getUnsavedPreviewObjectUrl({
                    doc_type: block.doc_type,
                    entity_id: block.hms_entity,
                    block_key: block.key,
                    value: buildValue(),
                }),
                `${block.label || 'Block'} preview.pdf`,
                `block-${block.id}`,
            );
        } finally {
            setPreviewing(false);
        }
    };

    const showPreviewBtn = canPreview && !isPending;

    const busy = saving !== null;

    // The head, the description and the legal note are all pinned above the
    // scrolling body, exactly as they were as siblings of the old panel — so
    // they go in the header slot together, not into `children`.
    const renderHeader = (titleNode) => (
        <>
            <div className="thq-modal-head">
                <div className="thq-modal-eyebrow">
                    {block.category_display}
                    <span className="thq-dot">·</span>
                    {block.format_display}
                    {block.hms_entity_name ? (
                        <>
                            <span className="thq-dot">·</span>
                            {block.hms_entity_name}
                        </>
                    ) : null}
                </div>
                {titleNode}
                <code className="thq-modal-key">{block.key}</code>
            </div>

            {block.description ? (
                <p className="thq-modal-desc">{block.description}</p>
            ) : null}

            {requiresApproval ? (
                <div className="thq-legal-note">
                    <FontAwesomeIcon icon={faScaleBalanced} />
                    <span>
                        This is legal content. Changes are versioned and must be approved by a
                        designated approver before they reach any generated document.
                    </span>
                </div>
            ) : null}
        </>
    );

    return (
        <HMSModal
            isOpen
            onClose={onClose}
            title={block.label}
            header={renderHeader}
            width={720}
            padded={false}
            showClose
            busy={busy}
            className="thq-block-editor"
            footer={(
                <div className="thq-modal-foot">
                    <HMSButton variant="secondary" size="compact" onClick={onClose} disabled={busy}>
                        Close
                    </HMSButton>
                    {!isPending && (
                        <div className="thq-modal-foot-primary">
                            {showPreviewBtn && (
                                <HMSButton
                                    variant="ghost" size="compact"
                                    onClick={handlePreview} disabled={busy || previewing || valueIsEmpty}
                                    title="Preview this content on a sample document (nothing is saved)"
                                >
                                    <FontAwesomeIcon icon={previewing ? faSpinner : faFilePdf} spin={previewing} />
                                    {previewing ? 'Preparing…' : 'Preview'}
                                </HMSButton>
                            )}
                            <HMSButton
                                variant="ghost" size="compact"
                                onClick={handleSaveDraft} disabled={busy}
                            >
                                <FontAwesomeIcon icon={faFloppyDisk} />
                                {saving === 'draft' ? 'Saving…' : 'Save draft'}
                            </HMSButton>
                            <HMSButton
                                variant="saveDraft" size="compact"
                                onClick={handleSubmit} disabled={busy}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                {saving === 'submit'
                                    ? 'Working…'
                                    : requiresApproval ? 'Submit for approval' : 'Publish'}
                            </HMSButton>
                        </div>
                    )}
                </div>
            )}
        >
            <div className="thq-modal-body">
                    {isPending ? (
                        <>
                            <div className="thq-banner thq-banner-warn">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                <div>
                                    <strong>A change is awaiting approval.</strong>
                                    <p>
                                        You cannot edit this block until the pending change is
                                        approved or rejected. The proposed content is shown below.
                                    </p>
                                </div>
                            </div>
                            <div className="thq-editor-section">
                                <div className="thq-editor-section-title">Proposed content</div>
                                <ValuePreview value={block.pending_version.value} format={block.content_format} />
                                {block.pending_version.change_note ? (
                                    <div className="thq-change-note-readout">
                                        <span className="thq-change-note-label">Change note</span>
                                        {block.pending_version.change_note}
                                    </div>
                                ) : null}
                            </div>
                        </>
                    ) : (
                        <>
                            {latestRejected ? (
                                <div className="thq-banner thq-banner-error">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <div>
                                        <strong>Previous change was rejected.</strong>
                                        <p>{latestVersion.rejection_reason || 'No reason was given.'}</p>
                                    </div>
                                </div>
                            ) : null}

                            <div className="thq-editor-section">
                                <div className="thq-editor-section-title">
                                    {isTermsList ? 'Clauses' : 'Content'}
                                </div>

                                {isTermsList ? (
                                    <div className="thq-terms-editor">
                                        {rows.map((row, index) => (
                                            <div key={index} className="thq-term-row">
                                                <select
                                                    className="thq-term-kind"
                                                    value={row.kind}
                                                    onChange={(e) => updateRow(index, { kind: e.target.value })}
                                                    disabled={busy}
                                                    aria-label="Clause type"
                                                >
                                                    {TERM_KINDS.map((k) => (
                                                        <option key={k.value} value={k.value}>{k.label}</option>
                                                    ))}
                                                </select>
                                                <textarea
                                                    className="thq-term-text"
                                                    value={row.text}
                                                    onChange={(e) => updateRow(index, { text: e.target.value })}
                                                    placeholder="Clause text…"
                                                    rows={2}
                                                    disabled={busy}
                                                />
                                                <div className="thq-term-actions">
                                                    <button
                                                        type="button" className="thq-icon-btn-sm"
                                                        onClick={() => moveRow(index, -1)}
                                                        disabled={busy || index === 0}
                                                        title="Move up" aria-label="Move up"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowUp} />
                                                    </button>
                                                    <button
                                                        type="button" className="thq-icon-btn-sm"
                                                        onClick={() => moveRow(index, 1)}
                                                        disabled={busy || index === rows.length - 1}
                                                        title="Move down" aria-label="Move down"
                                                    >
                                                        <FontAwesomeIcon icon={faArrowDown} />
                                                    </button>
                                                    <button
                                                        type="button" className="thq-icon-btn-sm thq-icon-btn-danger"
                                                        onClick={() => removeRow(index)}
                                                        disabled={busy || rows.length <= 1}
                                                        title="Remove clause" aria-label="Remove clause"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <HMSButton
                                            variant="ghost" size="compact"
                                            className="thq-add-row-btn"
                                            onClick={addRow} disabled={busy}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> Add clause
                                        </HMSButton>
                                    </div>
                                ) : (
                                    <textarea
                                        className="thq-plain-editor"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder={block.content_format === 'html'
                                            ? 'Raw HTML / ReportLab markup…'
                                            : 'Enter the content…'}
                                        rows={block.content_format === 'html' ? 12 : 6}
                                        spellCheck={block.content_format !== 'html'}
                                        disabled={busy}
                                    />
                                )}
                            </div>

                            <div className="thq-editor-section">
                                <label className="thq-field-label" htmlFor="thq-change-note">
                                    Change note <span className="thq-required">*</span>
                                </label>
                                <input
                                    id="thq-change-note"
                                    type="text"
                                    className="thq-change-note-input"
                                    value={changeNote}
                                    onChange={(e) => setChangeNote(e.target.value)}
                                    placeholder="Briefly describe what you changed and why"
                                    disabled={busy}
                                />
                            </div>
                        </>
                    )}

                    {/* Version history */}
                    <div className="thq-editor-section">
                        <div className="thq-editor-section-title">
                            <FontAwesomeIcon icon={faClockRotateLeft} className="thq-section-icon" />
                            Version history
                        </div>
                        {loadingVersions ? (
                            <div className="thq-history-empty">Loading history…</div>
                        ) : versions.length === 0 ? (
                            <div className="thq-history-empty">No versions yet.</div>
                        ) : (
                            <ul className="thq-history-list">
                                {versions.map((v) => (
                                    <li key={v.id} className="thq-history-item">
                                        <div className="thq-history-line">
                                            <span className="thq-history-ver">v{v.version_number}</span>
                                            <HMSBadge status={STATUS_TONE[v.status]}>
                                                {v.status === 'APPROVED' && v.is_active ? (
                                                    <><FontAwesomeIcon icon={faCircleCheck} /> Live</>
                                                ) : v.status_display}
                                            </HMSBadge>
                                            <span className="thq-history-meta">
                                                {v.created_by_name || 'Unknown'} · {formatTimestamp(v.created_at)}
                                            </span>
                                        </div>
                                        {v.change_note ? (
                                            <div className="thq-history-note">{v.change_note}</div>
                                        ) : null}
                                        {v.status === 'REJECTED' && v.rejection_reason ? (
                                            <div className="thq-history-rejection">
                                                Rejected: {v.rejection_reason}
                                            </div>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
            </div>
        </HMSModal>
    );
};

export default BlockEditorModal;
